import { ChartData, ChartDataset, ChartOptions, Point, ScriptableContext } from 'chart.js';
import { GenericChart, GenericChartProps } from 'app/agriculture/charts/GenericChart/GenericChart';
import { Line } from 'react-chartjs-2';

import { getDefaultLineChartOptions } from 'app/agriculture/charts/LineChart/DefaultLineChartOptions';
import { EmptyLineChart } from 'app/agriculture/charts/EmptyLineChart/EmptyLineChart';
import { isNaN, merge } from 'lodash';
import { ChartContext } from 'app/agriculture/charts/ChartContextProvider';
import { useContext } from 'react';

import { ChartLegend } from './ChartLegend';
import { isEqual, startOfHour } from 'date-fns';
import { useTimelineCursorTime } from 'app/agriculture/AgTimeline/context/TimelineContext';

export function MultiLineChart({
    title,
    displayUnit,
    chartOptionsOverride,
    datasets,
}: Omit<GenericChartProps, 'data'> & {
    chartOptionsOverride?: Partial<ChartOptions<'line'>>;
    datasets: { data: Record<string, number>; label: string; colour: string }[];
}) {
    const { intervalUnit, dateFormat } = useContext(ChartContext);
    const cursorTime = useTimelineCursorTime();
    const currentHour = cursorTime ? startOfHour(cursorTime) : startOfHour(new Date());
    // The regex removes milliseconds. See: https://stackoverflow.com/a/65076090
    const chartIsEmpty = datasets.every((dataset) =>
        Object.values(dataset.data).every((value) => isNaN(value)),
    );

    const firstData = datasets[0]?.data;

    if (firstData == null || chartIsEmpty) {
        return <EmptyLineChart title={title} />;
    }

    const chartDatasets: ChartDataset<'line', (number | Point | null)[]>[] = datasets.map(
        (dataset) => {
            function backgroundColor(context: ScriptableContext<'line'>): CanvasGradient {
                const { ctx: canvas } = context.chart;
                const gradient = canvas.createLinearGradient(0, 0, 0, context.chart.height);
                gradient.addColorStop(0, dataset.colour.replace('1)', '0.5)'));
                gradient.addColorStop(0.8, 'rgba(255, 255, 255, 0)');
                return gradient;
            }

            const values = Object.values(dataset.data);
            return {
                data: values,
                fill: true,
                pointStyle: 'circle',
                spanGaps: false,
                borderColor: dataset.colour,
                pointRadius: ({ dataIndex }: ScriptableContext<'line'>) => {
                    const timestamp = Object.keys(dataset.data)[dataIndex];
                    if (timestamp == null) {
                        throw Error(`No matching index for ${dataIndex}`);
                    }
                    const indexDate = new Date(timestamp);
                    if (isEqual(indexDate, currentHour)) {
                        return 8;
                    } else {
                        if (values[dataIndex - 1] == null && values[dataIndex + 1] == null) {
                            return 4;
                        } else {
                            return 0;
                        }
                    }
                },
                pointBorderWidth: 3,
                pointBorderColor: 'white',
                pointBackgroundColor: dataset.colour,
                tension: 0,
                backgroundColor,
            };
        },
    );

    const chartData: ChartData<'line'> = {
        labels: Object.keys(firstData),
        datasets: chartDatasets,
    };

    const chartOptions = merge(
        getDefaultLineChartOptions({
            intervalUnit: intervalUnit,
            yMeasurementUnit: displayUnit,
            dateFormat,
        }),
        chartOptionsOverride,
    );

    return (
        <GenericChart title={title}>
            <Line data={chartData} options={chartOptions} />
            <ChartLegend datasets={datasets} />
        </GenericChart>
    );
}
